import {z as zod} from "zod";
import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";

// Common metadata used across the system
export type CommonMetadata = {
    id: Uuid;
    revisionId: Uuid;
    createdAt: Iso8601DateTime;
    updatedAt: Iso8601DateTime;
    createdBy: Uuid;
    updatedBy: Uuid;
};

export enum BtlActivityTemplateWidgetType {
    // atomic widgets
    richText = "c1f7c9c4-7e7c-4d9e-9f6e-1a2b3c4d5e11",
    image = "8b2a1d63-5c44-4c1a-bb8c-6f4e92d77122",
    video = "5f0d8a92-3e5b-4e77-9a2d-9c7b8f1a3b33",
    file = "a7e3b4c1-2d11-4f5a-8c9e-0b7d6e5f4a44",
    table = "d2c8a0f7-6b99-4d21-a3e8-3f7c9b2a1c55",
    checklist = "9e1a7d5c-8f44-4b6c-9a11-5e3d2c7b6a66",
    dosAndDonts = "663ab07d-6582-4808-81d6-fe19ad1782ad",

    // layout widgets
    singleColumn = "f4b7e9a1-1c33-4d88-b2e1-7a9c5d3f2b77",
    twoColumns = "2a6d8f3c-9b55-4e11-8c77-1d4f6a9b8c88",
    threeColumns = "7c5a2e1b-3d22-4f99-a6b4-2e8d1c3a9d99",
};

/**
 * Checklist types
 */
export type BtlActivityTemplateChecklistItem =
    CommonMetadata & {
        content: string;
    };

export type BtlActivityTemplateChecklist =
    CommonMetadata & {
        title: string;
        checklistItems: Array<BtlActivityTemplateChecklistItem>;
    };

export type BtlActivityTemplateDosAndDontsItem =
    CommonMetadata & {
        content: string;
    };

// Widget types
export type BtlActivityTemplateRichTextWidget =
    CommonMetadata & {
        type: BtlActivityTemplateWidgetType.richText;
        content: string;
    };

export type BtlActivityTemplateImageWidget =
    CommonMetadata & {
        type: BtlActivityTemplateWidgetType.image;
        assetId: Uuid;
    };

export type BtlActivityTemplateVideoWidget =
    CommonMetadata & {
        type: BtlActivityTemplateWidgetType.video;
        assetId: Uuid;
    };

export type BtlActivityTemplateFileWidget =
    CommonMetadata & {
        type: BtlActivityTemplateWidgetType.file;
        assetId: Uuid;
    };

export type BtlActivityTemplateTableWidget =
    CommonMetadata & {
        title: string;
        type: BtlActivityTemplateWidgetType.table;
        content: Array<Array<string>>;
    };

export type BtlActivityTemplateChecklistWidget =
    CommonMetadata & {
        type: BtlActivityTemplateWidgetType.checklist;
        checklistId: Uuid;
    };

export type BtlActivityTemplateSingleColumnWidget =
    CommonMetadata & {
        type: BtlActivityTemplateWidgetType.singleColumn;
        children: Array<BtlActivityTemplateWidget>;
    };

export type BtlActivityTemplateTwoColumnsWidget =
    CommonMetadata & {
        type: BtlActivityTemplateWidgetType.twoColumns;
        children: Array<BtlActivityTemplateWidget>;
    };

export type BtlActivityTemplateThreeColumnsWidget =
    CommonMetadata & {
        type: BtlActivityTemplateWidgetType.threeColumns;
        children: Array<BtlActivityTemplateWidget>;
    };

export type BtlActivityTemplateDosAndDontsWidget = CommonMetadata & {
        type: BtlActivityTemplateWidgetType.dosAndDonts;
        dosItems: Array<BtlActivityTemplateDosAndDontsItem>;
        dontsItems: Array<BtlActivityTemplateDosAndDontsItem>;
    };

export type BtlActivityTemplateWidget =
    // atomic widgets
    | BtlActivityTemplateRichTextWidget
    | BtlActivityTemplateImageWidget
    | BtlActivityTemplateVideoWidget
    | BtlActivityTemplateFileWidget
    | BtlActivityTemplateTableWidget
    | BtlActivityTemplateChecklistWidget
    | BtlActivityTemplateDosAndDontsWidget
    // layout widgets
    | BtlActivityTemplateSingleColumnWidget
    | BtlActivityTemplateTwoColumnsWidget
    | BtlActivityTemplateThreeColumnsWidget;

// Root template
export type BtlActivityTemplate = CommonMetadata & {
    status: BtlActivityTemplateStatus;
    title: string;
    description: string;
    body: Array<BtlActivityTemplateWidget>;
    checklists: Array<BtlActivityTemplateChecklist>;
};
import {
    iso8601DateTimeZodValidator,
    uuidZodValidator,
} from "@vesta/global-common-typescript/validators/zodValidators";

export enum BtlActivityTemplateStatus {
    active = "e4f3f399-3ef0-4f9d-8d7b-9cecf9f9a3a3",
    deleted = "3ad27150-bde6-4cd5-82fe-bb19a7708a96",
    draft = "76b9de30-6976-42e5-bc02-688a648726a1",
};

export enum BtlActivityTemplateSectionLayoutOptions {
    single = "3c134f0b-6ff5-479c-947e-dc7417b7057e",
    twoColumns = "be609b30-adef-4ff2-a02e-aba624fee6ed",
    threeColumns = "f2d5ae78-d478-4c8b-b667-9bfc7c9cb0a8",
    grid = "d187e9e5-edd5-46ab-a126-728a4b4a9e8a",
}

enum AdditionalColumnType {
    text = "5b3314a7-31a6-4cc1-8642-6a4cfb2f03a1",
    email = "8c98683a-e056-42a4-8c19-c5a70a1353cc",
    uuid = "eeb6995c-af50-4898-9981-5d5708b59830",
    timestamp = "1f041a95-0735-4829-af0c-ee82caa5312b",
    select = "e010f1f3-3ae3-4e48-96e8-3dfba6b217c9",
}

export type BtlActivityTemplateData = CommonMetadata & {
    displayName: string;
    description: string;
    // TODO: Make all of these non-nullable ??
    body: Array<BtlActivityTemplateWidget> | null;
    checklists: Array<BtlActivityTemplateChecklist> | null;
    // TODO: Are these required?
    dispositions: Array<{
        id:Uuid;
        displayName:string;
    }> | null;
    defaultDisposition: Uuid | null;
    additionalColumns: Array<{
        id:Uuid,
        displayName:string,
        type:AdditionalColumnType,
        configuration:Record<string,unknown>
    }> | null;
    // schema: unknown | null;
};

export function btlActivityTemplateStatusToDisplayName(status: BtlActivityTemplateStatus): string {
    switch (status) {
        case BtlActivityTemplateStatus.active:
            return "Active";
        // case BtlActivityTemplateStatus.inactive:
        //     return "Inactive";
        case BtlActivityTemplateStatus.deleted:
            return "Deleted";
        case BtlActivityTemplateStatus.draft:
            return "Draft";
        default:
            return "Unknown";
    }
}

const btlActivityTemplateStatusZodValidator = zod.enum([
    BtlActivityTemplateStatus.active,
    // BtlActivityTemplateStatus.inactive,
    BtlActivityTemplateStatus.deleted,
    BtlActivityTemplateStatus.draft,
] as [BtlActivityTemplateStatus, ...Array<BtlActivityTemplateStatus>]);

const btlActivityTemplateChecklistItemZodSchema = zod.object({
    id: uuidZodValidator,
    revisionId: uuidZodValidator,
    createdAt: iso8601DateTimeZodValidator,
    updatedAt: iso8601DateTimeZodValidator,
    createdBy: uuidZodValidator,
    updatedBy: uuidZodValidator,
    content: zod.string(),
});

const btlActivityTemplateDosAndDontsItemZodSchema = zod.object({
    id: uuidZodValidator,
    revisionId: uuidZodValidator,
    createdAt: iso8601DateTimeZodValidator,
    updatedAt: iso8601DateTimeZodValidator,
    createdBy: uuidZodValidator,
    updatedBy: uuidZodValidator,
    content: zod.string(),
});

const btlActivityTemplateChecklistZodSchema: zod.ZodSchema<BtlActivityTemplateChecklist> = zod.object({
    id: uuidZodValidator,
    revisionId: uuidZodValidator,
    createdAt: iso8601DateTimeZodValidator,
    updatedAt: iso8601DateTimeZodValidator,
    createdBy: uuidZodValidator,
    updatedBy: uuidZodValidator,
    title: zod.string(),
    checklistItems: zod.array(btlActivityTemplateChecklistItemZodSchema),
});

const commonWidgetMetadataZodSchema = zod.object({
    id: uuidZodValidator,
    revisionId: uuidZodValidator,
    createdAt: iso8601DateTimeZodValidator,
    updatedAt: iso8601DateTimeZodValidator,
    createdBy: uuidZodValidator,
    updatedBy: uuidZodValidator,
});

// zod.lazy() enables the recursive reference: layout widgets' children can be any widget
const btlActivityTemplateWidgetZodSchema: zod.ZodType<BtlActivityTemplateWidget> = zod.lazy(() =>
    zod.discriminatedUnion("type", [
        btlActivityTemplateRichTextWidgetZodSchema,
        btlActivityTemplateImageWidgetZodSchema,
        btlActivityTemplateVideoWidgetZodSchema,
        btlActivityTemplateFileWidgetZodSchema,
        btlActivityTemplateTableWidgetZodSchema,
        btlActivityTemplateChecklistWidgetZodSchema,
        btlActivityTemplateDosAndDontsWidgetZodSchema,
        btlActivityTemplateSingleColumnWidgetZodSchema,
        btlActivityTemplateTwoColumnsWidgetZodSchema,
        btlActivityTemplateThreeColumnsWidgetZodSchema,
    ])
);

// Atomic widgets
const btlActivityTemplateRichTextWidgetZodSchema = commonWidgetMetadataZodSchema.extend({
    type: zod.literal(BtlActivityTemplateWidgetType.richText),
    content: zod.string(),
});

const btlActivityTemplateImageWidgetZodSchema = commonWidgetMetadataZodSchema.extend({
    type: zod.literal(BtlActivityTemplateWidgetType.image),
    assetId: uuidZodValidator,
});

const btlActivityTemplateVideoWidgetZodSchema = commonWidgetMetadataZodSchema.extend({
    type: zod.literal(BtlActivityTemplateWidgetType.video),
    assetId: uuidZodValidator,
});

const btlActivityTemplateFileWidgetZodSchema = commonWidgetMetadataZodSchema.extend({
    type: zod.literal(BtlActivityTemplateWidgetType.file),
    assetId: uuidZodValidator,
});

const btlActivityTemplateTableWidgetZodSchema = commonWidgetMetadataZodSchema.extend({
    type: zod.literal(BtlActivityTemplateWidgetType.table),
    title: zod.string(),
    content: zod.array(zod.array(zod.string())),
});

const btlActivityTemplateChecklistWidgetZodSchema = commonWidgetMetadataZodSchema.extend({
    type: zod.literal(BtlActivityTemplateWidgetType.checklist),
    checklistId: uuidZodValidator,
});

const btlActivityTemplateDosAndDontsWidgetZodSchema = commonWidgetMetadataZodSchema.extend({
    type: zod.literal(BtlActivityTemplateWidgetType.dosAndDonts),
    dosItems: zod.array(btlActivityTemplateDosAndDontsItemZodSchema),
    dontsItems: zod.array(btlActivityTemplateDosAndDontsItemZodSchema),
});

// Layout widgets - children recursively validate as any widget type
const btlActivityTemplateSingleColumnWidgetZodSchema = commonWidgetMetadataZodSchema.extend({
    type: zod.literal(BtlActivityTemplateWidgetType.singleColumn),
    children: zod.array(btlActivityTemplateWidgetZodSchema),
});

const btlActivityTemplateTwoColumnsWidgetZodSchema = commonWidgetMetadataZodSchema.extend({
    type: zod.literal(BtlActivityTemplateWidgetType.twoColumns),
    children: zod.array(btlActivityTemplateWidgetZodSchema),
});

const btlActivityTemplateThreeColumnsWidgetZodSchema = commonWidgetMetadataZodSchema.extend({
    type: zod.literal(BtlActivityTemplateWidgetType.threeColumns),
    children: zod.array(btlActivityTemplateWidgetZodSchema),
});

export const btlActivityTemplateDataApiResponseSchema: zod.ZodSchema<BtlActivityTemplateData> = zod.object({
    id: uuidZodValidator,
    revisionId: uuidZodValidator,
    status: btlActivityTemplateStatusZodValidator,
    createdAt: iso8601DateTimeZodValidator,
    updatedAt: iso8601DateTimeZodValidator,
    createdBy: uuidZodValidator,
    updatedBy: uuidZodValidator,
    displayName: zod.string(),
    description: zod.string(),
    body: zod.array(btlActivityTemplateWidgetZodSchema).nullable(),
    checklists: zod.array(btlActivityTemplateChecklistZodSchema).nullable(),
    dispositions: zod.array(zod.object({id: uuidZodValidator, displayName: zod.string()})).nullable(),
    defaultDisposition: uuidZodValidator.nullable(),
    additionalColumns: zod.array(zod.object({
        id: uuidZodValidator,
        displayName: zod.string(),
        type: zod.enum(AdditionalColumnType),
        configuration: zod.record(zod.string(), zod.unknown())
    })).nullable(),
    schema: zod.unknown(),
});

export const btlActivityTemplateDataArrayApiResponseSchema = zod.array(btlActivityTemplateDataApiResponseSchema);

// const LAYOUT_OPTIONS_ORDER: Array<BtlActivityTemplateSectionLayoutOptions> = [
//     BtlActivityTemplateSectionLayoutOptions.single,
//     BtlActivityTemplateSectionLayoutOptions.twoColumns,
//     BtlActivityTemplateSectionLayoutOptions.threeColumns,
//     BtlActivityTemplateSectionLayoutOptions.grid,
// ];

// export function getAdjacentLayoutOption(
//     current: BtlActivityTemplateSectionLayoutOptions,
//     direction: "next" | "previous",
// ): BtlActivityTemplateSectionLayoutOptions {
//     const index = LAYOUT_OPTIONS_ORDER.indexOf(current);
//     if (index === -1) {
//         return BtlActivityTemplateSectionLayoutOptions.single;
//     }
//     if (direction === "next") {
//         const nextIndex = index + 1;
//         if (nextIndex >= LAYOUT_OPTIONS_ORDER.length) {
//             return LAYOUT_OPTIONS_ORDER[0]!;
//         }
//         return LAYOUT_OPTIONS_ORDER[nextIndex]!;
//     }
//     const prevIndex = index - 1;
//     if (prevIndex < 0) {
//         return LAYOUT_OPTIONS_ORDER[LAYOUT_OPTIONS_ORDER.length - 1]!;
//     }
//     return LAYOUT_OPTIONS_ORDER[prevIndex]!;
// }

// export function getLayoutOptionClassName(layoutOption: BtlActivityTemplateSectionLayoutOptions): string {
//     switch (layoutOption) {
//         case BtlActivityTemplateSectionLayoutOptions.single:
//             return "grid grid-cols-1";
//         case BtlActivityTemplateSectionLayoutOptions.twoColumns:
//             return "grid md:grid-cols-[1fr_1fr] grid-rows-[1fr_1fr] gap-ds-space-250";
//         case BtlActivityTemplateSectionLayoutOptions.threeColumns:
//             return "grid md:grid-cols-[1fr_1fr_1fr] grid-rows-[1fr_1fr_1fr] gap-ds-space-250";
//         case BtlActivityTemplateSectionLayoutOptions.grid:
//             return "grid md:grid-cols-[1fr_1fr] grid-flow-row  gap-x-ds-space-250 gap-y-ds-space-200";
//         default:
//             return "grid grid-cols-1";
//     }
// }

// const LAYOUT_OPTIONS_ORDER: Array<BtlActivityTemplateSectionLayoutOptions> = [
//     BtlActivityTemplateSectionLayoutOptions.single,
//     BtlActivityTemplateSectionLayoutOptions.twoColumns,
//     BtlActivityTemplateSectionLayoutOptions.threeColumns,
// ];

// export function getAdjacentLayoutOption(
//     current: BtlActivityTemplateSectionLayoutOptions,
//     direction: "next" | "previous",
// ): BtlActivityTemplateSectionLayoutOptions {
//     const index = LAYOUT_OPTIONS_ORDER.indexOf(current);
//     if (index === -1) {
//         return BtlActivityTemplateSectionLayoutOptions.single;
//     }
//     if (direction === "next") {
//         const nextIndex = index + 1;
//         if (nextIndex >= LAYOUT_OPTIONS_ORDER.length) {
//             return LAYOUT_OPTIONS_ORDER[0]!;
//         }
//         return LAYOUT_OPTIONS_ORDER[nextIndex]!;
//     }
//     const prevIndex = index - 1;
//     if (prevIndex < 0) {
//         return LAYOUT_OPTIONS_ORDER[LAYOUT_OPTIONS_ORDER.length - 1]!;
//     }
//     return LAYOUT_OPTIONS_ORDER[prevIndex]!;
// }

export function getLayoutOptionClassName(layoutOption: BtlActivityTemplateSectionLayoutOptions): string {
    switch (layoutOption) {
        case BtlActivityTemplateSectionLayoutOptions.single:
            return "grid grid-cols-1 gap-ds-space-250";
        case BtlActivityTemplateSectionLayoutOptions.twoColumns:
            return "grid md:grid-cols-[1fr_1fr] gap-ds-space-250";
        case BtlActivityTemplateSectionLayoutOptions.threeColumns:
            return "grid md:grid-cols-[1fr_1fr_1fr] gap-ds-space-250";
        case BtlActivityTemplateSectionLayoutOptions.grid:
            return "grid md:grid-cols-[1fr_1fr] gap-ds-space-250";
        default:
            return "grid grid-cols-1";
    }
}

// export type CreateBtlActivityTemplatePayload = {
//     workspaceId: Uuid;
//     id: Uuid;
//     displayName: string;
//     slug: string;
//     description: string;
//     body:string;
//     dosAndDonts:{
//         dos:Array<{id:Uuid,content:string}>;
//         donts:Array<{id:Uuid,content:string}>;
//     }
//     checklist: BtlActivityTemplateChecklist;
//     resources: Array<Resource>;
//     dispositions: Array<{id:Uuid,displayName:string}>;
//     defaultDisposition: Uuid;
//     additionalColumns: Array<{
//         id:Uuid,
//         displayName:string,
//         type:AdditionalColumnType,
//         configuration:Record<string,unknown>
//     }>;
// };

export type BtlActivityTemplateTable = {
    title: string;
    columns: Array<{field: Uuid; headerName: string}>;
    rows: Array<Record<Uuid, string>>;
};

export type BtlUiChecklistData = {
    title: string;
    items: Array<{id: Uuid; content: string}>;
};

export type BtlActivityTemplateDosAndDonts = {
    dosItems: Array<{id: Uuid; content: string}>;
    dontsItems: Array<{id: Uuid; content: string}>;
};

// Builder UI Types
export type BtlUiImageItem = {
    id: string;
    file: File;
    previewUrl: string;
    croppedUrl?: string;
    addedAtIso: string;
};

export type BtlUiVideoItem =
    | {id: string; kind: "file"; file: File; previewUrl: string}
    | {id: string; kind: "url"; url: string};

export type BtlUiFileItem = {
    id: string;
    file: File;
    previewUrl: string;
    addedAtIso: string;
};
export type BtlUiMarkdownBlock = {
    id: Uuid;
    type: "markdown";
    family: "card";
    content: string;
    error: string | null;
};

export type BtlUiChecklistBlock = {
    id: Uuid;
    type: "checklist";
    family: "card";
    data: BtlUiChecklistData;
    error: string;
};

export type BtlUiDosAndDontsBlock = {
    id: Uuid;
    type: "dosAndDonts";
    family: "card";
    data: BtlActivityTemplateDosAndDonts;
    error: string | null;
};

export type BtlUiFileBlock = {
    id: Uuid;
    type: "file";
    family: "media";
    data: BtlUiFileItem | null;
    error: string | null;
};

export type BtlUiImageBlock = {
    id: Uuid;
    type: "image";
    family: "media";
    data: BtlUiImageItem | null;
    error: string | null;
};

export type BtlUiVideoBlock = {
    id: Uuid;
    type: "video";
    family: "media";
    data: BtlUiVideoItem | null;
    error: string | null;
};

export type BtlUiTableBlock = {
    id: Uuid;
    type: "table";
    family: "card";
    data: BtlActivityTemplateTable;
    error: string | null;
};

export type BtlUiBlock = BtlUiMarkdownBlock | BtlUiChecklistBlock | BtlUiDosAndDontsBlock | BtlUiFileBlock | BtlUiImageBlock | BtlUiVideoBlock | BtlUiTableBlock;
